import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../common/user";
import { betListService } from "../../../services/agent/betList.service";
import { setGeneralProgressHide, setGeneralProgressShow, setGeneralSnackbarState } from "../../common/ui";


export type payloadSportList = {
  playerId: string | number | undefined;
  payload: {
    betId?: string;
    betType?: string | number;
    betResult?: string | number;
    sportTypeKey?: string | number;
    dateFrom?: string;
    dateTo?: string;
    limit?: number | string;
    after?: string;
  }
};

export type payloadCasinoList = {
  playerId: string | number | undefined;
  payload: {
    roundId?: string;
    productType?: string | number;
    gameProviderId?: string | number;
    gameProviderName?: string;
    gameId?: string;
    gameName?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number | string;
    after?: string;
  }
};

export type columnsItem = {
  name: string;
  type: string;
  sort: boolean;
  match?: boolean;
};

export type listItem = {
  [key: string]: any;
};

export interface ITableData {
  items: listItem[];
  columns: columnsItem[];
  totalCount: number | string;
}

export type betListSliceState = {
  sport: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  casino: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
};

const initialState: betListSliceState = {
  sport: {
    data: null,
    error: null,
    isFetching: false,
  },
  casino: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getSportListData: any = createAsyncThunk(
  'agent/betList/getSportListData',
  async (payload: payloadSportList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await betListService.getSportList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getCasinoListData: any = createAsyncThunk(
  'agent/betList/getCasinoListData',
  async (payload: payloadCasinoList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await betListService.getCasinoList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        
        if (data?.error === 'common.errors.agent.bet-history.unable_to_retrieve_bet_history') {
          dispatch(setGeneralSnackbarState({
            open: true,
            messageKey: 'common.errors.agent.bet-history.unable_to_retrieve_bet_history',
            type: 'error'
          }));
        }
        
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const betListSlice = createSlice({
  name: 'betList',
  initialState: initialState,
  reducers: {
    clearSportListState(state) {
      state.sport.error = null;
      state.sport.isFetching = false;
      state.sport.data = null;
    },
    clearCasinoListState(state) {
      state.casino.error = null;
      state.casino.isFetching = false;
      state.casino.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<betListSliceState>>) => {
    // Sport bet list
    builder.addCase(getSportListData.pending, (state ) => {
      state.sport.isFetching = true;
      state.sport.error = null;
    });
    builder.addCase(getSportListData.fulfilled, (state , action) => {
      state.sport.error = null;
      state.sport.isFetching = false;

      const hasAfter = action.meta?.arg?.payload.hasOwnProperty('after') && action.meta?.arg?.payload?.after

      if (hasAfter && state.sport.data) {
        state.sport.data.items = [
          ...(state.sport.data.items || []),
          ...(action.payload.items || []),
        ];
      } else {
        state.sport.data = action.payload;
      }
    });
    builder.addCase(getSportListData.rejected, (state , action) => {
      state.sport.isFetching = false;
      state.sport.error = action.payload;
      state.sport.data = null;
    });

    // Casino bet list
    builder.addCase(getCasinoListData.pending, (state ) => {
      state.casino.isFetching = true;
      state.casino.error = null;
    });
    builder.addCase(getCasinoListData.fulfilled, (state , action) => {
      state.casino.error = null;
      state.casino.isFetching = false;

      const hasAfter = action.meta?.arg?.payload.hasOwnProperty('after') && action.meta?.arg?.payload?.after

      if (hasAfter && state.casino.data) {
        state.casino.data.items = [
          ...(state.casino.data.items || []),
          ...(action.payload.items || []),
        ];
      } else {
        state.casino.data = action.payload;
      }
    });
    builder.addCase(getCasinoListData.rejected, (state , action) => {
      state.casino.isFetching = false;
      state.casino.error = action.payload;
      state.casino.data = null;
    });
  },
});

export const {
  clearSportListState,
  clearCasinoListState,
} = betListSlice.actions;

export default betListSlice.reducer;
